import { useEffect, useState, type ComponentType } from 'react';
import { api, isApiError } from '@/lib/api';
import { logger } from '@/lib/logger';

export interface AuthProviderInfo {
  id: string;
  label: string;
  enabled: boolean;
}

export interface UseAuthProvidersState {
  providers: AuthProviderInfo[];
  loading: boolean;
}

/**
 * Reads `GET /auth/providers`, which reports each OAuth provider's `enabled`
 * flag from its real credentials.
 *
 * The sign-in and sign-up pages used to hardcode both OAuth buttons as live
 * links, so on a fresh clone — where DISCORD_CLIENT_ID / GOOGLE_CLIENT_ID are
 * empty placeholders — clicking them hit an endpoint that can only 403. The
 * backend already knew they were unavailable; the UI just wasn't asking.
 *
 * When the request fails we return an empty list and the caller falls back to
 * rendering every button as enabled, because hiding sign-in options on a
 * transient network error would be worse than a failed click.
 */
export function useAuthProviders(): UseAuthProvidersState {
  const [providers, setProviders] = useState<AuthProviderInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    api
      .get<{ providers: AuthProviderInfo[] }>('/auth/providers')
      .then((res) => {
        if (cancelled) return;
        if (!isApiError(res) && Array.isArray(res.data?.providers)) {
          setProviders(res.data.providers);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        logger.warn('auth/providers request failed — assuming all OAuth enabled', {
          message: (err as Error).message,
        });
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { providers, loading };
}

/** `true` when the id is known and explicitly reported as disabled. */
export function isProviderDisabled(
  providers: AuthProviderInfo[],
  id: string,
): boolean {
  return providers.some((p) => p.id === id && !p.enabled);
}

/**
 * `true` when the backend reports email/password sign-in as disabled, i.e. the
 * platform is OAuth-only. Returns `false` while providers are still loading (or
 * if the request failed) so the form shows by default on a transient error.
 */
export function isManualAuthDisabled(providers: AuthProviderInfo[]): boolean {
  return isProviderDisabled(providers, 'manual');
}

interface OAuthButtonSpec {
  id: string;
  label: string;
  href: string;
  Icon: ComponentType<{ size?: number; className?: string }>;
  iconColor?: string;
}

/** Mirrors `OAuthButtons`' own props: enabled = `href`, disabled = `disabledReason`. */
export interface ResolvedOAuthButton {
  id: string;
  label: string;
  Icon: ComponentType<{ size?: number; className?: string }>;
  iconColor?: string;
  href?: string;
  disabledReason?: string;
}

/**
 * Turns a provider definition into an `OAuthButtons` entry: a live link when the
 * backend reports the provider as enabled, or a disabled button with an
 * explanation when it reports `enabled: false`.
 */
export function buildOAuthButton(
  providers: AuthProviderInfo[],
  spec: OAuthButtonSpec,
  unavailableReason: string,
): ResolvedOAuthButton {
  if (!isProviderDisabled(providers, spec.id)) return spec;
  const { id, label, Icon, iconColor } = spec;
  return { id, label, Icon, iconColor, disabledReason: unavailableReason };
}